import { useMutation, useQuery } from '@tanstack/react-query';

import { makeAPIRequestReactQuery } from './api';

export interface ReferralAttachment {
  id: string;
  referralId: string;
  fileName: string;
  attachmentDate?: string; // ISO date string
  path: string;
  fileType?: string;
  status: 'PENDING' | 'UPLOADED';
}

export interface CreateAttachmentResponse {
  id: string;
  uploadUrl: string;
}

export interface UpdateAttachmentRequest {
  fileName?: string;
  attachmentDate?: string;
}

export interface DeleteAttachmentsRequest {
  attachmentIds: string[];
}

// Get attachments for a referral
// GET /referrals/:referralId/attachments
export const useReferralAttachments = (referralId: string) => {
  return useQuery({
    queryKey: ['referralAttachments', referralId],
    queryFn: async (): Promise<{ attachments: ReferralAttachment[] }> => {
      return await makeAPIRequestReactQuery<{
        attachments: ReferralAttachment[];
      }>(`/referrals/${referralId}/attachments`, {}, 'GET');
    },
    enabled: !!referralId,
  });
};

// Create attachment and get upload URL
// POST /referrals/:referralId/attachments
export const useCreateAttachment = () => {
  return useMutation({
    mutationFn: async ({
      referralId,
      fileName,
      attachmentDate,
    }: {
      referralId: string;
      fileName: string;
      attachmentDate?: string; // ISO string
    }): Promise<CreateAttachmentResponse> => {
      return await makeAPIRequestReactQuery<CreateAttachmentResponse>(
        `/referrals/${referralId}/attachments`,
        undefined,
        'POST',
        {
          data: {
            fileName,
            attachmentDate,
          },
        },
      );
    },
  });
};

// Update attachment metadata
// PUT /referrals/:referralId/attachments/:attachmentId
export const useUpdateAttachment = () => {
  return useMutation({
    mutationFn: async ({
      referralId,
      attachmentId,
      data,
    }: {
      referralId: string;
      attachmentId: string;
      data: UpdateAttachmentRequest;
    }): Promise<void> => {
      await makeAPIRequestReactQuery<void>(
        `/referrals/${referralId}/attachments/${attachmentId}`,
        undefined,
        'PUT',
        {
          data,
        },
      );
    },
  });
};

// Delete attachments
// POST /referrals/:referralId/deleteAttachments
export const useDeleteAttachments = () => {
  return useMutation({
    mutationFn: async ({
      referralId,
      attachmentIds,
    }: {
      referralId: string;
      attachmentIds: string[];
    }): Promise<void> => {
      await makeAPIRequestReactQuery<void>(
        `/referrals/${referralId}/deleteAttachments`,
        undefined,
        'POST',
        {
          data: {
            attachmentIds,
          },
        },
      );
    },
  });
};

// Upload file to S3 using presigned URL
export const uploadFileToS3 = async (
  uploadUrl: string,
  file: File,
): Promise<void> => {
  const response = await fetch(uploadUrl, {
    method: 'PUT',
    body: file,
    headers: {
      'Content-Type': file.type,
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to upload file: ${response.statusText}`);
  }
};
